import { Controller } from 'stimulus'
import { useDispatch } from 'stimulus-use'
import template from 'lodash.template'
import get from 'lodash/get'
import { keys } from '@app/components/atomic/constants'

/**
 * @namespace MultiselectTypeahead
 * {@link https://adamsilver.io/blog/building-an-accessible-autocomplete-control/}
 */
export default class extends Controller {
  static targets = ['fieldLabel', 'inputInteractive', 'selectionsList', 'selectionsListAnnouncement', 'selectionPillTemplate', 'selectionPillInstance', 'selectionPillLabel']

  /**
     * Convert the contents of the template tag into a Lodash template
     * {@link https://lodash.com/docs/4.17.15#template}
     */
  connect() {
    useDispatch(this)
    this.selectionPillTemplate = template(this.selectionPillTemplateTarget.innerHTML)
  }

  /**
     * Get the number of options currently selected
     * @returns {number}
     */
  getExistingValuesCount() {
    return $(this.selectionsListTarget).children().length
  }

  /**
     * Determine whether or not a given option has already been selected.
     * @param {string} optionValue
     * @param {string} inputName
     * @returns {boolean}
     */
  optionHasNotBeenSelected(optionValue, inputName) {
    return $(`input[name="${inputName}"][value="${optionValue}"]`).length === 0
  }

  /**
     * Create a removable pill element, and append it to the selections list
     * @param {HTMLLIElement} target
     * {@link https://mikerogers.io/2020/06/07/how-to-interpolate-templates-in-stimulus}
     */
  createSelectionPill(target) {
    const label = this.selectionPillLabelTargets.find(label => label.closest('li') === target.closest('li')) || target
    const liData = target.closest('li').dataset
    const rawItem = liData.raw ? JSON.parse(liData.raw) : {}
    const name = liData.inputName || this.selectionsListTarget.dataset.inputName
    const value = liData.value || target.dataset.value
    const text = liData.pillText || rawItem.text || label.textContent.trim()

    const pillData = {
      text: text,
      index: $(this.selectionsListTarget).children().length + 1,
      name,
      value,
      party_code: get(rawItem, 'metadata.party_code')
    }

    const pill = this.selectionPillTemplate(pillData)
    $(this.selectionsListTarget).append(pill)
  }

  /**
     * Update the contents of the live region, so that we can tell screen reader users about changes in selections
     * @param {string} message
     */
  updateLiveRegion(message) {
    const count = this.getExistingValuesCount()
    if (this.hasFieldLabelTarget) { // TODO - This should be present and skipping breaks the announcement
      const labelText = $(this.fieldLabelTarget).text().trim()
      $(this.selectionsListAnnouncementTarget).text(`${message} ${labelText} selections. You now have ${count} ${labelText} selected.`)
    }
  }

  /**
     * De-select all of the options in one go
     */
  deselectAll() {
    $('.molecule-pill-removable').remove()
    this.updateLiveRegion('Removed all')
  }

  /**
     * De-select an option
     * @param {Event} event
     */
  deselectOption(event) {
    const deselectedItemLabel = $(event.target).prev().text().trim()
    this.updateLiveRegion(`Removed “${deselectedItemLabel}” from`)
    this.dispatch('optionDeselected')
  }

  /**
     * Select an option --but only if it hasn't been selected yet.
     * @param {Event} event
     */
  selectOption(event) {
    const inputName = event.target.dataset.inputName || event.target.closest('li').dataset.inputName || this.selectionsListTarget.dataset.inputName
    const value = event.target.dataset.value || event.target.closest('li').dataset.value

    if (this.optionHasNotBeenSelected(value, inputName)) {
      this.createSelectionPill(event.target)
      this.updateLiveRegion(`Added “${event.target.textContent.trim()}” to`)
      $(this.inputInteractiveTarget).trigger('clear')
      this.dispatch('optionSelected', { value })
    }
  }

  /**
     * Determine what to do when the user presses a keyboard key on an option
     * @param {KeyboardEvent} event
     * {@link https://adamsilver.io/blog/building-an-accessible-autocomplete-control/#menu-interaction-via-keyboard}
     */
  handleOptionKeydown(event) {
    // Grab event.key for IE11
    const key = event.code || event.key
    if (key === keys.ENTER || key === keys.SPACE) {
      event.preventDefault()
      this.selectOption(event)
    }
  }

  /**
     * Focus on the next element that should receive browser focus.
     * @param {CustomEvent} event
     * @see {RemovablePill.disconnect}
     */
  manageBrowserFocus(event) {
    const triggerInputElement = $(event.detail.pillRemovableParent).closest(this.element).find(this.inputInteractiveTarget)
    const focusElement = event.detail.nextActiveElement || triggerInputElement
    $(focusElement).focus()
  }
}
